import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'
import FormDatePickerInput from '../../../../../components/Forms/FormDatePickerInput'
import FormInput from '../../../../../components/Forms/FormInput'

interface BatchModalProps {
  open: boolean
  onClose: () => void
  onSave: (data: BatchData) => void
  initialData?: BatchData | null
}

export interface BatchData {
  id?: string
  internalId?: string
  batchNumber: string
  expiryDate: string
  productId?: number
  validFrom?: string
  validTo?: string
}

export const batchSchema = z.object({
  batchNumber: z
    .string()
    .min(1, { message: 'Batch Number is required' })
    .max(50, { message: 'Batch Number be more than 200 characters' }),
  expiryDate: z.preprocess(
    (val) => {
      if (val instanceof Date) return val
      if (typeof val === 'string') return new Date(val)
      return undefined
    },
    z
      .date()
      .refine(
        (date) => !isNaN(date.getTime()) && date >= new Date(new Date().setHours(0, 0, 0, 0)),
        { message: 'Expiry Date must be today or in the future' }
      )
  ),
})

export type BatchFormData = {
  batchNumber: string
  expiryDate: Date
}

const BatchModal = ({ open, onClose, onSave, initialData }: BatchModalProps) => {
  const methods = useForm<BatchFormData>({
    resolver: zodResolver(batchSchema),
    defaultValues: initialData
      ? {
        batchNumber: initialData.batchNumber || '',
        expiryDate: initialData.expiryDate ? new Date(initialData.expiryDate) : new Date(),
      }
      : {
        batchNumber: '',
        expiryDate: new Date(),
      },
    mode: 'onTouched',
  })

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = methods

  useEffect(() => {
    reset(
      initialData
        ? {
          batchNumber: initialData.batchNumber || '',
          expiryDate: initialData.expiryDate ? new Date(initialData.expiryDate) : new Date(),
        }
        : {
          batchNumber: '',
          expiryDate: new Date(),
        }
    )
  }, [initialData, open, reset])

  const onSubmit = (data: BatchFormData) => {
    const formattedData: BatchData = {
      ...data,
      expiryDate: data.expiryDate.toISOString().split('T')[0],
    }
    onSave(
      initialData && initialData.internalId
        ? { ...formattedData, internalId: initialData.internalId }
        : formattedData
    )
    reset({
      batchNumber: '',
      expiryDate: new Date(),
    })
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{initialData ? 'Edit Batch' : 'Add Batch'}</DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <FormInput
                  name='batchNumber'
                  label='Batch Number'
                  placeholder='Enter batch number'
                  fullWidth
                  required
                  maxLength={50}
                  inputPattern={/[<>{}]/g}
                  inputPatternMessage='Special characters such as < > { } are not allowed'
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <FormDatePickerInput
                  name='expiryDate'
                  label='Expiry Date'
                  format='YYYY-MM-DD'
                  required
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                reset({
                  batchNumber: '',
                  expiryDate: new Date(),
                })
                onClose()
              }}
            >
              Cancel
            </Button>
            <Button type='submit' variant='contained' color='primary'>
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}

export default BatchModal
